/** Microsoft Clarity — session recordings / heatmaps. Loaded unless analytics consent is rejected. */

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: IArguments[] };
  }
}

let clarityInitialized = false;
let missingIdLogged = false;

function clarityScriptPresent(): boolean {
  return Boolean(document.querySelector('script[src*="clarity.ms/tag/"]'));
}

/** Load the Clarity tag once. Safe to call repeatedly. */
export function initClarity(projectId: string | undefined): void {
  const id = projectId?.trim();
  if (!id) {
    if (import.meta.env.DEV && !missingIdLogged) {
      missingIdLogged = true;
      console.info(
        "[analytics] VITE_CLARITY_ID is not set — Clarity will not load. Set it in .env and restart Vite / rebuild for production.",
      );
    }
    return;
  }
  if (typeof document === "undefined") return;
  if (clarityInitialized || clarityScriptPresent()) {
    clarityInitialized = true;
    return;
  }

  // Official Clarity bootstrap (must push `arguments`, not a rest array).
  (function (c: Window, l: Document, a: "clarity", r: "script", i: string) {
    c[a] =
      c[a] ||
      function (this: void) {
        // eslint-disable-next-line prefer-rest-params
        (c[a]!.q = c[a]!.q || []).push(arguments);
      };
    const t = l.createElement(r);
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode!.insertBefore(t, y);
  })(window, document, "clarity", "script", id);

  clarityInitialized = true;
}

/** Consent Mode v2 signal for Clarity (ad_Storage / analytics_Storage casing required). */
export function syncClarityConsent(granted: boolean): void {
  if (typeof window.clarity !== "function") return;
  window.clarity("consentv2", {
    ad_Storage: "denied",
    analytics_Storage: granted ? "granted" : "denied",
  });
}

/**
 * When analytics is allowed: load Clarity (if ID set) and grant analytics_Storage.
 * When rejected: send denied if Clarity is already present (does not unload the script).
 */
export function applyClarityForConsent(granted: boolean): void {
  if (granted) {
    initClarity(import.meta.env.VITE_CLARITY_ID as string | undefined);
    syncClarityConsent(true);
  } else {
    syncClarityConsent(false);
  }
}
